/**
 * The shared list filter bar's model (T27) — pure functions, no React.
 *
 * A list's filters are its URL query parameters, a flat string map whose keys
 * are the list endpoint's own parameter names (`status`, `start_date`, …). The
 * URL, the request and a saved filter (Odoo favourite) are that same map, with
 * one difference: a saved filter keeps a preset date range as `period`
 * ('this_week') instead of the dates, so it stays relative — "This week" saved
 * today still means this week when it is opened next week.
 *
 * Every date is a YYYY-MM-DD calendar day on the STORE's clock (T18): the
 * caller passes the zone the API filters the list on (the filtered store's,
 * else the business's), never this browser's.
 */

export type DatePresetId =
  | 'today'
  | 'yesterday'
  | 'this_week'
  | 'last7'
  | 'this_month'
  | 'last30'
  | 'last_month';

export const DATE_PRESETS: { id: DatePresetId; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'this_week', label: 'This week' },
  { id: 'last7', label: 'Last 7 days' },
  { id: 'this_month', label: 'This month' },
  { id: 'last30', label: 'Last 30 days' },
  { id: 'last_month', label: 'Last month' },
];

const PRESET_IDS = DATE_PRESETS.map((p) => p.id) as string[];

export function isDatePreset(value: string | null | undefined): value is DatePresetId {
  return !!value && PRESET_IDS.includes(value);
}

/** The key a saved filter keeps a preset range under. */
export const PERIOD_KEY = 'period';

/** Pagination is never part of a filter: applying one always opens page 1. */
export const PAGING_KEYS = ['page', 'limit'];

export type FilterValues = Record<string, string | undefined>;

/** Where a list keeps its date range in the URL / request. */
export interface FilterDateKeys {
  fromKey: string;
  toKey: string;
}

// ─── Store-clock calendar arithmetic ─────────────────────────────────────────

/** YYYY-MM-DD now on `timeZone`'s clock. */
export function todayInZone(timeZone: string, now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

/** Shift a YYYY-MM-DD by N days — calendar arithmetic, no zone. */
export function shiftDay(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** The preset's first and last day, inclusive, on the store's calendar. Weeks start on Monday. */
export function presetRange(
  preset: DatePresetId,
  timeZone: string,
  now: Date = new Date(),
): { from: string; to: string } {
  const today = todayInZone(timeZone, now);
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case 'yesterday': {
      const y = shiftDay(today, -1);
      return { from: y, to: y };
    }
    case 'this_week': {
      const weekday = new Date(`${today}T00:00:00Z`).getUTCDay(); // 0 = Sunday
      return { from: shiftDay(today, -((weekday + 6) % 7)), to: today };
    }
    case 'last7':
      return { from: shiftDay(today, -6), to: today };
    case 'this_month':
      return { from: `${today.slice(0, 8)}01`, to: today };
    case 'last30':
      return { from: shiftDay(today, -29), to: today };
    case 'last_month': {
      const lastDay = shiftDay(`${today.slice(0, 8)}01`, -1);
      return { from: `${lastDay.slice(0, 8)}01`, to: lastDay };
    }
  }
}

/** The preset whose days are exactly from–to today, if any (the first in list order). */
export function matchPreset(
  from: string | undefined,
  to: string | undefined,
  timeZone: string,
  now: Date = new Date(),
  presets: DatePresetId[] = PRESET_IDS as DatePresetId[],
): DatePresetId | null {
  if (!from || !to) return null;
  for (const id of presets) {
    const r = presetRange(id, timeZone, now);
    if (r.from === from && r.to === to) return id;
  }
  return null;
}

/** 2026-10-25 → 25/10/2026. */
export function formatDay(date: string): string {
  const [y, m, d] = date.split('-');
  return `${d}/${m}/${y}`;
}

// ─── URL ⇄ filter values ─────────────────────────────────────────────────────

/** The list's filter values from its URL — only the keys the page knows. */
export function readFilterValues(params: URLSearchParams, keys: string[]): Record<string, string> {
  const values: Record<string, string> = {};
  for (const key of keys) {
    const v = params.get(key);
    if (v) values[key] = v;
  }
  return values;
}

/** Apply a change to the URL: empty values are removed, and any filter change goes back to page 1. */
export function applyFilterPatch(params: URLSearchParams, patch: FilterValues): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined || value === '') next.delete(key);
    else next.set(key, value);
  }
  if (Object.keys(patch).some((k) => !PAGING_KEYS.includes(k))) next.delete('page');
  return next;
}

/** Remove every filter the page knows (and the page number); other parameters stay. */
export function clearFilterParams(params: URLSearchParams, keys: string[]): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of [...keys, 'page']) next.delete(key);
  return next;
}

// ─── Saved filters (favourites) ──────────────────────────────────────────────

/** What a favourite stores: no paging, no empties, and a preset range as `period`. */
export function toSavedFilter(
  values: FilterValues,
  date: FilterDateKeys | undefined,
  timeZone: string,
  now: Date = new Date(),
): Record<string, string> {
  const saved: Record<string, string> = {};
  for (const [key, value] of Object.entries(values)) {
    if (value && !PAGING_KEYS.includes(key)) saved[key] = value;
  }
  if (date) {
    const preset = matchPreset(saved[date.fromKey], saved[date.toKey], timeZone, now);
    if (preset) {
      delete saved[date.fromKey];
      delete saved[date.toKey];
      saved[PERIOD_KEY] = preset;
    }
  }
  return saved;
}

/** The URL values a favourite opens: `period` becomes that period's days now. */
export function fromSavedFilter(
  saved: Record<string, string>,
  date: FilterDateKeys | undefined,
  timeZone: string,
  now: Date = new Date(),
): Record<string, string> {
  const values: Record<string, string> = { ...saved };
  const period = values[PERIOD_KEY];
  delete values[PERIOD_KEY];
  if (date && isDatePreset(period)) {
    const r = presetRange(period, timeZone, now);
    values[date.fromKey] = r.from;
    values[date.toKey] = r.to;
  }
  return values;
}

/** Same filters, whatever the key order. */
export function sameFilters(a: Record<string, string>, b: Record<string, string>): boolean {
  const ka = Object.keys(a).filter((k) => a[k]);
  const kb = Object.keys(b).filter((k) => b[k]);
  return ka.length === kb.length && ka.every((k) => a[k] === b[k]);
}

// ─── Search facets (Odoo) ────────────────────────────────────────────────────

/** One way to read the search text: 'Customer', 'Mobile', 'Bill no.' … */
export interface FilterBarFacet {
  /** The list endpoint's parameter, e.g. `customer_phone`. */
  key: string;
  label: string;
  /**
   * What the facet holds, which decides the order the suggestions come in so
   * Enter picks the likely one: digits alone → phone, then number facets;
   * letters with digits (INV-12, PO-2026…) → number facets; words → text.
   */
  kind?: 'text' | 'phone' | 'number';
}

export function looksNumeric(text: string): boolean {
  return /^\+?[\d\s-]+$/.test(text.trim());
}

const FACET_ORDER: Record<
  'phone' | 'code' | 'words',
  Array<NonNullable<FilterBarFacet['kind']>>
> = {
  phone: ['phone', 'number', 'text'],
  code: ['number', 'text', 'phone'],
  words: ['text', 'number', 'phone'],
};

/** The facets to offer for what was typed, the likely one first. */
export function facetSuggestions(text: string, facets: FilterBarFacet[]): FilterBarFacet[] {
  const t = text.trim();
  if (!t) return [];
  const order = FACET_ORDER[looksNumeric(t) ? 'phone' : /\d/.test(t) ? 'code' : 'words'];
  return [...facets].sort(
    (a, b) => order.indexOf(a.kind ?? 'text') - order.indexOf(b.kind ?? 'text'),
  );
}
